export interface CampingSite {
  id: string;
  title: string;
  street: string;
  city: string;
  state: string;
  zipcode: string;
  phone: string;
  country: string;
  latitude: number;
  longitude: number;
  shortDescription: string;
  longDescription: string;
  website: string;
  photo: string;
  photos?: string[]; // Optional array of additional photos
  amenities: string;
  rate: string;
  status: string;
  featured: boolean;
  fullSites: string;
  partialSites: string;
  pullThruSites: string;
  maxRvLength: string;
  maxElectrical: string;
  checkIn: string;
  checkOut: string;
  wifi: string;
  dumpStation: string;
  pets: string;
  petRestrictions: string;
  peakDates: string;
  drivingDirections: string;
  areaAttractions: string;
  rentalCabins: string;
  rentalParkModels: string;
}

// Import all resorts from the index file
import { allResorts } from '../data/resorts/index';

// Cache for loaded data
let cachedSites: CampingSite[] | null = null;

// Load and parse camping sites from JSON files
export const getCampingSites = async (): Promise<CampingSite[]> => {
  if (cachedSites) {
    return cachedSites;
  }

  cachedSites = allResorts as CampingSite[];
  return cachedSites;
};

// Synchronous version for components that need immediate data
export const getCampingSitesSync = (): CampingSite[] => {
  return cachedSites || [];
};

// Get unique states for filtering
export const getStates = (sites: CampingSite[]): string[] => {
  const states = sites.map(site => site.state).filter(Boolean);
  return [...new Set(states)].sort();
};

// Get unique cities for filtering
export const getCities = (sites: CampingSite[]): string[] => {
  const cities = sites.map(site => site.city).filter(Boolean);
  return [...new Set(cities)].sort();
};

// Get unique countries for filtering
export const getCountries = (sites: CampingSite[]): string[] => {
  const countries = sites.map(site => {
    // Normalize country names - convert USA to United States
    if (site.country === 'USA' || site.country === 'United States') {
      return 'United States';
    }
    return site.country;
  }).filter(Boolean);
  return [...new Set(countries)].sort();
};

// Filter sites by state
export const filterByState = (sites: CampingSite[], state: string): CampingSite[] => {
  if (!state || state === 'All States') return sites;
  return sites.filter(site => site.state === state);
};

// Filter sites by city
export const filterByCity = (sites: CampingSite[], city: string): CampingSite[] => {
  if (!city || city === 'All Cities') return sites;
  return sites.filter(site => site.city === city);
};

// Filter sites by country
export const filterByCountry = (sites: CampingSite[], country: string): CampingSite[] => {
  if (!country || country === 'All Countries') return sites;
  return sites.filter(site => {
    // Normalize country names for comparison
    const siteCountry = site.country === 'USA' ? 'United States' : site.country;
    return siteCountry === country;
  });
};

// Filter sites by name
export const filterByName = (sites: CampingSite[], name: string): CampingSite[] => {
  if (!name) return sites;
  return sites.filter(site => 
    site.title.toLowerCase().includes(name.toLowerCase())
  );
};

// Get featured sites
export const getFeaturedSites = (sites: CampingSite[]): CampingSite[] => {
  return sites.filter(site => site.featured);
};